import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Copy, UserPlus } from 'lucide-react';
import { tenantAPI } from '../../api/client';
import { seatsUsed, UNLIMITED_MEMBERS } from '../../lib/limits';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { apiError, appUrl, goTo } from '../../lib/host';
import { ErrorBanner, formatDate, Modal, PageHeading, RolePill, StatCards, useBasic } from './ui';

const ROLES = ['owner', 'admin', 'member', 'viewer'];
const RANK = { viewer: 0, member: 1, admin: 2, owner: 3 };
const ROLE_HELP = {
  owner: 'Full control, including billing',
  admin: 'Manages team, settings and contacts',
  member: 'Works with contacts',
  viewer: 'Read-only access',
};
// Mirrors backend permissions.can_manage; the server enforces it regardless.
const canManage = (actor, target) => actor === 'owner' || (RANK[actor] >= RANK.admin && target !== 'owner');

function InviteForm({ grantable, onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [link, setLink] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await tenantAPI.invite(email, role);
      setLink(res.data.invite_url);
      onInvited(email);
    } catch (err) {
      setError(apiError(err, 'Could not send the invitation'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Invite a teammate"
      onClose={onClose}
      footer={link
        ? <button className="primary-action" onClick={onClose}>Done</button>
        : <>
          <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
          <button type="submit" form="invite-form" className="primary-action" disabled={busy}>{busy ? 'Sending…' : 'Send invitation'}</button>
        </>}
    >
      {link ? (
        <div className="invite-done">
          <p>Invitation sent to <b>{email}</b>. You can also share this link with them directly; it expires in 7 days.</p>
          <div className="copy-row">
            <input readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Invitation link" />
            <button className="secondary-action" onClick={() => navigator.clipboard?.writeText(link)}><Copy size={13} />Copy</button>
          </div>
        </div>
      ) : (
        <form id="invite-form" className="form-grid" onSubmit={send}>
          <ErrorBanner message={error} />
          <label className="field span-2">Email<input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus placeholder="teammate@example.com" /></label>
          <label className="field span-2">Role
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {grantable.map((r) => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)} · {ROLE_HELP[r]}</option>)}
            </select>
          </label>
        </form>
      )}
    </Modal>
  );
}

export default function TeamPage() {
  const { user, refresh } = useAuth();
  const { tenant, role: myRole, can } = useTenant();
  const { notify, upgrade, plan, refreshMembers } = useBasic();
  const [members, setMembers] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    tenantAPI.members().then((res) => setMembers(res.data)).catch((err) => setError(apiError(err)));
    if (can('members.invite')) tenantAPI.invitations().then((res) => setInvites(res.data)).catch(() => {});
    refreshMembers();
  }, [can, refreshMembers]);

  useEffect(load, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      if (ok) notify(ok);
      load();
      return true;
    } catch (err) {
      setError(apiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!window.confirm(`Leave ${tenant.name}? You'll lose access immediately.`)) return;
    if (await run(() => tenantAPI.leave())) {
      await refresh();
      goTo(appUrl('/select-org'));
    }
  };

  const grantable = ROLES.filter((r) => RANK[r] <= RANK[myRole]);
  const active = members?.filter((m) => m.status === 'active').length;
  const admins = members?.filter((m) => m.status === 'active' && RANK[m.role] >= RANK.admin).length;
  const seatsLeft = active == null ? null : Math.max(0, tenant.max_members - active - invites.length);

  return (
    <section className="page-view">
      <PageHeading title="Team" subtitle={`Manage who can access ${tenant.name} and what they can do.`}>
        {can('members.invite') && <button className="primary-action" onClick={() => setInviting(true)}><UserPlus size={14} />Invite teammate</button>}
      </PageHeading>

      <ErrorBanner message={error} />

      <StatCards stats={[
        ['Team members', active == null ? null : seatsUsed(active, tenant.max_members), `${plan} limit`],
        ['Owners & admins', admins, 'Can manage the team'],
        ['Invitations pending', can('members.invite') ? invites.length : '—', 'Awaiting response'],
        ['Seats available', seatsLeft, seatsLeft === 0 ? 'Upgrade for more seats' : 'Ready to invite'],
      ]} />

      <section className="panel page-table-panel">
        <div className="panel-heading">
          <div><h2>Members of {tenant.name}</h2><small className="table-subtitle">{members === null ? 'Loading…' : `${members.length} member${members.length === 1 ? '' : 's'}`}</small></div>
        </div>
        <div className="table-scroll">
          <table className="full-page-table">
            <thead><tr><th>Member</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th>Action</th></tr></thead>
            <tbody>
              {members?.map((m) => {
                const self = m.user_id === user.id;
                const manageable = !self && can('members.update_role') && canManage(myRole, m.role);
                return (
                  <tr key={m.user_id}>
                    <td><b className="cell-strong">{m.name}</b>{self && <small className="cell-note">You</small>}</td>
                    <td>{m.email}</td>
                    <td>
                      {manageable ? (
                        <select className="table-select" value={m.role} disabled={busy} aria-label={`Role for ${m.name}`}
                          onChange={(e) => run(() => tenantAPI.updateMember(m.user_id, { role: e.target.value }), `${m.name} is now ${e.target.value}.`)}>
                          {grantable.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : <RolePill role={m.role} />}
                    </td>
                    <td><span className={`status ${m.status === 'active' ? 'green' : 'red'}`}>{m.status === 'active' ? 'Active' : 'Suspended'}</span></td>
                    <td>{formatDate(m.joined_at)}</td>
                    <td>
                      <div className="row-actions">
                        {manageable && (
                          <button className="row-action" disabled={busy}
                            onClick={() => run(() => tenantAPI.updateMember(m.user_id, { status: m.status === 'active' ? 'suspended' : 'active' }),
                              `${m.name} ${m.status === 'active' ? 'suspended' : 'reactivated'}.`)}>
                            {m.status === 'active' ? 'Suspend' : 'Reactivate'}
                          </button>
                        )}
                        {manageable && can('members.remove') && (
                          <button className="row-action danger" disabled={busy}
                            onClick={() => window.confirm(`Remove ${m.name} from ${tenant.name}?`) && run(() => tenantAPI.removeMember(m.user_id), `${m.name} removed.`)}>
                            Remove
                          </button>
                        )}
                        {self && <button className="row-action danger" disabled={busy} onClick={leave}>Leave company</button>}
                        {!self && !manageable && <span className="cell-muted">—</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {can('members.invite') && (
        <section className="panel page-table-panel section-gap">
          <div className="panel-heading">
            <div><h2>Pending invitations</h2><small className="table-subtitle">Invitations expire after 7 days</small></div>
          </div>
          <div className="table-scroll">
            <table className="full-page-table">
              <thead><tr><th>Email</th><th>Role</th><th>Expires</th><th>Action</th></tr></thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td><RolePill role={i.role} /></td>
                    <td>{formatDate(i.expires_at)}</td>
                    <td><button className="row-action danger" disabled={busy} onClick={() => run(() => tenantAPI.revokeInvitation(i.id), `Invitation to ${i.email} revoked.`)}>Revoke</button></td>
                  </tr>
                ))}
                {invites.length === 0 && <tr><td colSpan={4} className="empty-state">No pending invitations.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="page-bottom-grid">
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Roles</h2></div>
          <div className="role-list">
            {ROLES.map((r) => <div key={r}><RolePill role={r} /><span>{ROLE_HELP[r]}</span></div>)}
          </div>
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Plan access</h2><span className="active-pill"><i />{plan}</span></div>
          <p className="access-copy">{tenant.max_members >= UNLIMITED_MEMBERS ? `${plan} has no team size limit.` : `${plan} includes up to ${tenant.max_members} team members.`} Upgrade for a bigger team.</p>
          <button className="upgrade-full" onClick={upgrade}>Review subscription <ArrowRight size={14} /></button>
        </section>
      </div>

      {inviting && <InviteForm grantable={grantable} onClose={() => { setInviting(false); load(); }} onInvited={(email) => notify(`Invitation sent to ${email}.`)} />}
    </section>
  );
}
